import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function LeadCaptureForm() {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    interest: '',
    email: '',
    whatsapp: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const interestOptions = t('leadForm.form.interestOptions', { returnObjects: true }) as string[];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await fetch('https://readdy.ai/api/form/submit/arieira-lead-capture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString()
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', company: '', interest: '', email: '', whatsapp: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="consultation" className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-4xl mx-auto px-8">
        <div className="bg-white rounded-3xl p-12 shadow-2xl">
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-amber-100 rounded-full">
              <i className="ri-file-list-3-line text-3xl text-amber-700"></i>
            </div>
            <p className="text-sm font-bold text-gray-500 tracking-wider uppercase mb-3">
              {t('leadForm.badge')}
            </p>
            <h2 className="text-4xl font-serif text-gray-900 mb-4">
              {t('leadForm.title')} <span className="italic">{t('leadForm.titleItalic')}</span>
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t('leadForm.description')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="arieira-lead-capture">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('leadForm.form.name')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('leadForm.form.company')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="interest" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('leadForm.form.interest')}
              </label>
              <select
                id="interest"
                name="interest"
                value={formData.interest}
                onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">{t('leadForm.form.interestPlaceholder')}</option>
                {interestOptions.map((option, index) => (
                  <option key={index} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('leadForm.form.email')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label htmlFor="whatsapp" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('leadForm.form.whatsapp')}
                </label>
                <input
                  type="tel"
                  id="whatsapp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full px-8 py-4 bg-amber-600 text-white text-base font-semibold rounded-full hover:bg-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {t('leadForm.form.button')}...
                </span>
              ) : (
                t('leadForm.form.button')
              )}
            </button>

            {status === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 text-center">
                  {t('leadForm.form.success')}
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 text-center">
                  {t('leadForm.form.error')}
                </p>
              </div>
            )}

            <p className="text-xs text-gray-500 text-center">
              {t('leadForm.form.privacy')}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}
